import { useSyncExternalStore } from "react";

// Asks the workspace home's deck of panes to show a workspace in one of them:
// the sidebar's rows and anything else that opens a workspace (a chat's
// investigation, a trace's Workspace button) open one there rather than over
// the page. A request made before the deck is on screen waits for it.
const listeners = new Set();
let waiting = [];

export function onDeckWorkspace(fn) {
  listeners.add(fn);
  const held = waiting;
  waiting = [];
  for (const [id, extra] of held) fn(id, extra);
  return () => {
    listeners.delete(fn);
  };
}

export const deckShown = () => listeners.size > 0;

// `extra`: what the opener holds for the board ({ trace, focus }); with no
// `id`, `{ fresh: true }` asks for a pane to start a new workspace in.
export function showWorkspaceInDeck(id, extra = null) {
  if (!id) return false;
  if (!listeners.size) {
    waiting = [...waiting.filter(([x]) => x !== id), [id, extra]].slice(-4);
    return false;
  }
  for (const fn of listeners) fn(id, extra);
  return true;
}

// A new workspace: the pane being worked in, emptied to start one (what it
// showed stays kept, in the sidebar).
export function startWorkspaceInDeck() {
  if (!listeners.size) {
    waiting = [...waiting.filter(([x]) => x != null), [null, { fresh: true }]].slice(-4);
    return false;
  }
  for (const fn of listeners) fn(null, { fresh: true });
  return true;
}

// Whether the pane being worked in shows no workspace: the range's board,
// where sessions are picked into the search to start one.
let blankInFront = false;
const blankListeners = new Set();
export function setDeckBlankInFront(value) {
  const next = !!value;
  if (next === blankInFront) return;
  blankInFront = next;
  for (const fn of blankListeners) fn();
}
const subscribeBlank = (fn) => {
  blankListeners.add(fn);
  return () => blankListeners.delete(fn);
};
const readBlank = () => blankInFront;
export function useDeckBlankInFront() {
  return useSyncExternalStore(subscribeBlank, readBlank, readBlank);
}
